import React from "react";
import { motion } from "framer-motion";

const pillars = [
  {
    key: "food",
    kicker: "Food Intelligence",
    title: "Every meal, scored for your body.",
    body: "Camera + barcode + pantry — meals get a compatibility score weighted by your biomarkers and daily state.",
    icon: "leaf",
    color: "#2DD4BF",
    span: "md:col-span-2",
  },
  {
    key: "bio",
    kicker: "Biomarker Intelligence",
    title: "Every lab marker, decoded and trended.",
    body: "Upload years of blood reports. We extract, normalize and turn them into a moving picture, not a snapshot.",
    icon: "vial",
    color: "#14B8A6",
  },
  {
    key: "mind",
    kicker: "Mind Intelligence",
    title: "Mood, stress, loneliness — tracked without asking.",
    body: "Signals from sleep, HRV, meal timing, and self check-ins reveal the emotional patterns your body reacts to.",
    icon: "brain",
    color: "#A78BFA",
  },
  {
    key: "sleep",
    kicker: "Sleep Intelligence",
    title: "Not just hours slept. The recovery you actually got.",
    body: "Depth, timing, HRV rebound, next-day cravings — sleep quantified against what it did for your metabolism.",
    icon: "moon",
    color: "#C4B5FD",
    span: "md:col-span-2",
  },
  {
    key: "ai",
    kicker: "Predictive AI",
    title: "Risk, before it becomes a diagnosis.",
    body: "A private model that watches your trajectory across food, biomarkers, mood and sleep — and flags drift months early.",
    icon: "spark",
    color: "#FB7185",
    span: "md:col-span-2",
  },
];

const Ic = ({ name, color }) => {
  const c = { width: 20, height: 20, viewBox: "0 0 24 24", fill: "none", stroke: color, strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round" };
  const m = {
    leaf: <svg {...c}><path d="M11 20A7 7 0 0 1 4 13l9-9 7 7a7 7 0 0 1-9 9Z"/><path d="M2 22 22 2"/></svg>,
    vial: <svg {...c}><path d="M9 3h6M10 3v6L4 21h16L14 9V3"/></svg>,
    brain: <svg {...c}><path d="M9 4a3 3 0 0 0-3 3v1a3 3 0 0 0-2 5 3 3 0 0 0 2 5v1a3 3 0 0 0 3 3M15 4a3 3 0 0 1 3 3v1a3 3 0 0 1 2 5 3 3 0 0 1-2 5v1a3 3 0 0 1-3 3M9 4v18M15 4v18"/></svg>,
    moon: <svg {...c}><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" /></svg>,
    spark: <svg {...c}><path d="m12 3 2 5 5 2-5 2-2 5-2-5-5-2 5-2z" /></svg>,
  };
  return m[name];
};

export default function Pillars() {
  return (
    <section id="pillars" data-testid="pillars-section" className="relative py-24 md:py-32">
      <div className="absolute inset-0 -z-10">
        <div className="absolute left-1/2 -top-20 h-[420px] w-[720px] -translate-x-1/2 bg-radial-teal opacity-70" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl">
          <div className="text-[11px] uppercase tracking-[0.22em] text-teal-soft">03 — Why HealthIRL</div>
          <h2 className="mt-4 font-heading text-4xl sm:text-5xl tracking-tighter leading-[1.05] font-medium">
            Five intelligences.
            <br /><span className="gradient-text-teal-lav">One operating system for your health.</span>
          </h2>
        </div>

        <div className="mt-12 grid md:grid-cols-4 gap-4">
          {pillars.map((p, i) => (
            <motion.div key={p.key}
              initial={{ y: 20, opacity: 0 }} whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true, margin: "-8% 0px" }} transition={{ delay: (i % 4) * 0.06, duration: 0.6 }}
              data-testid={`pillar-${p.key}`}
              className={`group relative col-span-2 ${p.span || ""} rounded-2xl border border-white/[0.07] bg-ink-800/60 p-7 overflow-hidden hover:-translate-y-1 transition-all duration-300`}
              style={{ boxShadow: `inset 0 0 0 1px transparent` }}>
              <div className="absolute -top-24 -right-24 h-56 w-56 rounded-full opacity-30 blur-3xl group-hover:opacity-60 transition-opacity"
                style={{ background: `radial-gradient(circle at center, ${p.color}55 0%, transparent 70%)` }} />
              <div className="relative flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg border border-white/10 bg-white/[0.03] flex items-center justify-center">
                  <Ic name={p.icon} color={p.color} />
                </div>
                <div className="text-[11px] uppercase tracking-[0.22em]" style={{ color: p.color }}>{p.kicker}</div>
              </div>
              <h3 className="relative mt-5 font-heading text-2xl tracking-tight leading-tight">{p.title}</h3>
              <p className="relative mt-3 text-sm text-zinc-400 leading-relaxed max-w-lg">{p.body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
